// Copyright (C) 2021 Bystronic Laser AG

import { EChartsOption } from 'echarts';
import { FilterTimeAxisSpanEnum } from '../enums/FilterTimeAxisSpanEnum';
import { FilterTimeSpanEnum } from '../enums/FilterTimeSpanEnum';
import { ChartGenerator, ProcedureName } from './abstract/chartGenerator';
import { GeneratorParams } from './generatorParams';
import { BendTrendComparativeProductivitySummaryData } from './chartsData';
import { metricsService } from '@/services/metrics.service';
import { getDefaultEchartColorAt } from '@/utils/color';

export default class BendTrendComparativeProductivitySummaryGenerator extends ChartGenerator<BendTrendComparativeProductivitySummaryData> {
  constructor(procedure: ProcedureName, private readonly customerIdDh: number) {
    super(procedure);
  }

  override async getData(
    selectedDevices: string[],
    selectedShifts: number[],
    timeSpan: FilterTimeSpanEnum | [string, string],
    timeAxisSpan?: FilterTimeAxisSpanEnum,
    params?: { [key: string]: any },
    customerNo?: string,
  ): Promise<BendTrendComparativeProductivitySummaryData | null> {
    const dateRange = timeSpan as [string, string];
    return await metricsService.getBendingDevicesMetrics(
      this.procedure,
      {
        customerIdDh: this.customerIdDh,
        startDate: dateRange[0],
        endDate: dateRange[1],
        shiftIds: selectedShifts,
        deviceStringIds: selectedDevices,
      },
      this.controller,
    );
  }

  override updateOptions(
    data: BendTrendComparativeProductivitySummaryData,
    parameters: GeneratorParams,
    prevOptions?: EChartsOption,
  ): EChartsOption {
    const sortedData = data.productivityPerDevice.sort((a, b) =>
      a.deviceStringId.localeCompare(b.deviceStringId),
    );
    return {
      grid: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
      },
      xAxis: {
        type: 'value',
        max: data.productivityPerDevice
          .map((x) => x.productivityPercentage)
          .reduce((a, b) => a + b, 0),
      },
      yAxis: {
        type: 'category',
      },
      series: sortedData
        .map((x, index) => ({
          name: x.deviceStringId,
          type: 'bar' as const,
          stack: 'stack',
          barWidth: '100%',
          label: {
            show: false,
          },
          itemStyle: {
            color: getDefaultEchartColorAt(index),
          },
          data: [x.productivityPercentage],
          emphasis: {
            disabled: true,
          },
        }))
        .sort((a, b) => a.data[0] - b.data[0]),
    };
  }
}
